import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import isPropValid from "@emotion/is-prop-valid";
import { Typography } from "@mui/material";
import { SubscriptionStatus } from "@api";

const MessageText = styled(Typography, {
  shouldForwardProp: (prop: string) => isPropValid(prop) || prop !== "color",
})<{ color: "alert" | "info" | "success" | "error" }>`
  width: fit-content;
  display: inline-block;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing(1.75, 2)};
  color: ${({ theme, color }) => {
    switch (color) {
      case "success":
        return theme.palette.text.positive;
      case "alert":
        return theme.palette.text.warning;
      case "error":
        return theme.palette.text.negative;
      default:
        return theme.palette.text.upgrade;
    }
  }};
  background-color: ${({ theme, color }) => {
    switch (color) {
      case "success":
        return theme.palette.background.success;
      case "alert":
        return theme.palette.background.warning;
      case "error":
        return theme.palette.background.error;
      default:
        return theme.palette.background.upgrade;
    }
  }};
`;

interface Props {
  status: SubscriptionStatus | "processing_at_bank_reduced";
}

const MessageColorStatusMap: Record<
  SubscriptionStatus | "processing_at_bank_reduced",
  "alert" | "info" | "success" | "error"
> = {
  in_progress: "alert",
  incomplete: "alert",
  internal_processing: "info",
  processing_at_bank: "info",
  processing_at_bank_reduced: "success",
  processed: "success",
  contract_terminated: "error",
  cancelled: "success",
};

export const CashbackNotificationStatusMessage = ({ status }: Props) => {
  const { t } = useTranslation();

  const color = MessageColorStatusMap[status];

  return (
    <MessageText variant="body3" color={color}>
      {t(`dashboardPage.cashbackCard.statusMessage.${status}`)}
    </MessageText>
  );
};
